import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';

interface ProgressStage {
  message: string;
  done: boolean;
}

@Component({
  selector: 'piqet-angular-report-progress',
  templateUrl: './report-progress.component.html',
  styleUrls: ['./report-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Emulated encapsulation can de-scope @keyframes names from animation-name
  // references, leaving the CSS animations silently disabled. The BEM
  // report-progress__* class names won't collide with anything else.
  encapsulation: ViewEncapsulation.None,
})
export class ReportProgressComponent implements OnChanges {
  // Latest status text streamed from the backend. Each distinct value is captured
  // into the {@link stages} timeline; the previous stage flips to "done" so the
  // user can see what's already finished and what's running right now.
  @Input() message = '';
  @Input() title = 'Generating your report';
  @Input() subtitle = "This may take a few moments. Please don't close this window.";

  // How many recent stages to keep on screen. Older ones scroll out of view so
  // the card stays compact regardless of how many backend messages arrive.
  private readonly maxVisible = 4;

  stages: ProgressStage[] = [];

  // Tail-slice of the stage timeline used by the template. The full list is
  // kept in {@link stages} so we never re-add a message we've already seen.
  get visibleStages(): ProgressStage[] {
    return this.stages.length > this.maxVisible
      ? this.stages.slice(-this.maxVisible)
      : this.stages;
  }

  get hasHiddenStages(): boolean {
    return this.stages.length > this.maxVisible;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['message']) return;
    const incoming = (changes['message'].currentValue ?? '').toString().trim();
    if (!incoming) return;

    const last = this.stages[this.stages.length - 1];
    if (last && last.message === incoming) return;

    if (last) last.done = true;
    this.stages = [...this.stages, { message: incoming, done: false }];
  }

  trackByMessage(_index: number, stage: ProgressStage): string {
    return stage.message;
  }
}
